<template>
  <div class="py-2 px-4">
    <div class="overflow-hidden border-b border-gray-200 rounded-lg shadow">
      <div class="px-6 py-2 text-gray-500 bg-gray-50 border-b border-gray-200">
        Search for Gift Cards
      </div>
      <div class="px-6 py-4 space-y-4 text-gray-500 bg-white">
        <div class="flex flex-row space-x-4">
          <div>
            <icon-fgt :name="selectedLogo" class="w-32 h-32" />
          </div>
          <div class="w-full">
            <form @submit.prevent="searchGiftCard" class="space-y-4">
              <div>
                <label :for="`cart-select-`" class="block text-sm font-medium leading-5 text-gray-700">Select Cart</label>
                <select
                  :id="`cart-select-`"
                  v-model="cartId"
                  class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                >
                  <option
                    v-for="cart in carts"
                    :key="cart.ID"
                    :value="cart.ID"
                  >
                    {{ cart.CartName }}
                  </option>
                </select>
              </div>
              <div>
                <label :for="`gift-card-${_uid}`" class="block text-sm font-medium leading-5 text-gray-700 mt-2">
                  Last 4 characters of the Gift Card <span class="text-gray-500 font-normal">(case sensitive)</span>
                </label>
                <div class="relative rounded-md shadow-sm">
                  <input
                    :id="`gift-card-${_uid}`"
                    v-model="giftCard"
                    class="form-input block w-full sm:text-sm sm:leading-5"
                    placeholder="Search..."
                  />
                </div>
              </div>
              <div class="flex justify-end">
                  <Button
                    type="submit"
                    :disabled="! canSearch"
                    class="flex items-center space-x-2"
                  >
                    <span>Search</span>
                    <LoadingSpinner v-if="processing" />
                  </Button>
              </div>
            </form>
          </div>
        </div>

        <HttpError :error="error" dismissable />

        <div v-if="! processing">
          <div v-if="results.length" class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table class="w-full">
              <thead class="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cart
                  </th>
                  <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Characters
                  </th>
                  <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer ID
                  </th>
                  <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date issued
                  </th>
                  <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Remaining / Value
                  </th>
                </tr>
              </thead>
              <tr
                v-for="(result, index) in results"
                :key="index"
                :class="{
                  'bg-white': ! (index % 2),
                  'bg-gray-50': index % 2
                }"
              >
                <td class="px-6 py-4 whitespace-nowrap text-center tracking-wider">
                  {{ result.cart_id | cartName }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-center tracking-wider font-medium text-gray-900">
                  {{ result.last_characters }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <GiftCardStatus :status="getGiftCardStatus(result)" />
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-center tracking-wider">
                  {{ result.customer_id }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-center tracking-wider">
                  {{ result.created_at | date }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-center tracking-wider">
                  {{ result.balance | money }} / {{ result.initial_value | money }}
                </td>
              </tr>
            </table>
          </div>
          <div v-else-if="searched" class="text-center">
            No results found.
          </div>
        </div>

      </div>
    </div>
  </div>

</template>
<script>
import allCarts from "@/data/carts.json";
import { ApiFactory } from "@/api/ApiFactory.js";
import GiftCardStatus from "./GiftCardStatus.vue";
import dateFormatter from "@/mixins/dateFormatter";
import moneyFormatter from "@/mixins/moneyFormatter";
import Button from "@/components/elements/Button.vue";
import giftCardStatuses from "@/data/giftCardStatuses.js";

const GiftCards = ApiFactory.get("gift-cards");

export default {
  name: "SearchGiftCards",
  components: {
    Button,
    GiftCardStatus
  },
  filters: {
    date: dateFormatter.longMonth,
    money: moneyFormatter.format,
    cartName(id) {
      return allCarts.find((cart) => cart.ID === Number(id))?.CartName;
    },
  },
  data() {
    return{
      cartId: null,
      giftCard: null,
      results: [],
      error: null,
      processing: false,
      searched: false
    }
  },
  computed: {
    carts() {
      return allCarts.filter((cart) => [22, 23, 31, 32].includes(cart.ID));
    },
    selectedCart() {
      return this.carts.find((cart) => cart.ID === this.cartId);
    },
    selectedLogo() {
      return this.selectedCart?.LogoName;
    },
    canSearch() {
      if (! this.giftCard) {
        return false;
      }

      if (this.processing) {
        return false;
      }

      return String(this.giftCard).length === 4;
    }
  },
  created() {
    this.cartId = this.carts[0].ID;
  },
  methods: {
    searchGiftCard() {
      this.error = null;
      this.processing = true;

      GiftCards.search({
          cartId: this.selectedCart?.ID,
          characters: this.giftCard,
        })
        .then((response) => {
          this.results = response.data.body;
          this.resetForm();
        })
        .catch((error) => this.error = error)
        .finally(() => {
          this.processing = false
          this.searched = true;
        });
    },
    resetForm() {
      this.giftCard = null;
    },
    getGiftCardStatus(giftCard) {
      if (giftCard.disabled_at) {
        return giftCardStatuses.Disabled;
      }

      let now = (new Date()).getTime();

      let end = (giftCard?.expires_on)
        // expires_on is in the YYYY-mm-dd format and the card expires at the end of that day
        ? (new Date(giftCard?.expires_on)).getTime() + 24 * 60 * 60 * 1000
        // add enough time in the future to mimick that the card doesn't have an expiration date
        : now + 100 * 24 * 60 * 60 * 1000;

      if (now > end) {
        return giftCardStatuses.Expired;
      }

      if (end - now < 32 * 24 * 60 * 60 * 1000) {
        return giftCardStatuses.Expiring;
      }

      return giftCardStatuses.Enabled;
    }
  }
};
</script>
